<div class="schedule-dialog" data-testid="schedule-dialog">
  <header>
    <h2 mat-dialog-title class="schedule-dialog__title">{{ 'testRunSchedule.dialog.title' | translate }}</h2>
    <p class="schedule-dialog__subtitle">
      @if (data.runKey) {
        <code>{{ data.runKey }}</code>
      }
      {{ data.runName }}
    </p>
  </header>

  <mat-dialog-content class="schedule-dialog__content">
    @switch (loadState()) {
      @case ('loading') {
        <p class="schedule-dialog__loading" data-testid="schedule-loading">
          <mat-progress-spinner mode="indeterminate" diameter="16" />
          {{ 'testRunSchedule.dialog.loading' | translate }}
        </p>
      }
      @case ('failed') {
        <div class="schedule-dialog__error" role="alert" data-testid="schedule-error">
          <mat-icon aria-hidden="true">error</mat-icon>
          <span>{{ 'testRunSchedule.dialog.loadError' | translate }}</span>
          <button
            mat-stroked-button
            type="button"
            class="schedule-dialog__retry"
            data-testid="schedule-retry"
            (click)="load()"
          >
            {{ 'common.retry' | translate }}
          </button>
        </div>
      }
      @default {
        <fieldset class="schedule-dialog__field">
          <legend class="schedule-dialog__label">{{ 'testRunSchedule.dialog.weekdays' | translate }}</legend>
          <div class="schedule-dialog__weekdays">
            @for (day of weekdayOptions; track day) {
              <button
                type="button"
                class="schedule-dialog__weekday"
                data-testid="schedule-weekday"
                [class.schedule-dialog__weekday--on]="isSelected(day)"
                [attr.aria-pressed]="isSelected(day)"
                [attr.aria-label]="'testRunSchedule.weekdayLong.' + day | translate"
                (click)="toggleWeekday(day)"
              >
                {{ 'testRunSchedule.weekday.' + day | translate }}
              </button>
            }
          </div>
          <div class="schedule-dialog__presets">
            <button
              type="button"
              class="schedule-dialog__preset"
              data-testid="schedule-preset-workdays"
              (click)="preset(workdays)"
            >
              {{ 'testRunSchedule.preset.workdays' | translate }}
            </button>
            <button
              type="button"
              class="schedule-dialog__preset"
              data-testid="schedule-preset-daily"
              (click)="preset(weekdayOptions)"
            >
              {{ 'testRunSchedule.preset.daily' | translate }}
            </button>
          </div>
        </fieldset>

        <div class="schedule-dialog__row">
          <div class="schedule-dialog__field">
            <label class="schedule-dialog__label" for="schedule-time">
              {{ 'testRunSchedule.dialog.time' | translate }}
            </label>
            <input
              id="schedule-time"
              type="time"
              class="schedule-dialog__input"
              data-testid="schedule-time"
              required
              [value]="time()"
              (input)="onTimeInput($event)"
            />
          </div>
          <div class="schedule-dialog__field schedule-dialog__field--grow">
            <label class="schedule-dialog__label" for="schedule-zone">
              {{ 'testRunSchedule.dialog.zone' | translate }}
            </label>
            <select
              id="schedule-zone"
              class="schedule-dialog__input"
              data-testid="schedule-zone"
              required
              (change)="onZoneChange($event)"
            >
              @for (option of zones(); track option) {
                <option [value]="option" [selected]="option === zone()">{{ option }}</option>
              }
            </select>
          </div>
        </div>

        <div class="schedule-dialog__field">
          <app-runner-target-select
            [initial]="initialTarget()"
            (targetChange)="target.set($event)"
            (connect)="cancel()"
          />
          @if (targetMissing()) {
            <p class="schedule-dialog__hint" data-testid="schedule-target-missing">
              <mat-icon aria-hidden="true">warning</mat-icon>
              {{ 'testRunSchedule.dialog.targetMissing' | translate }}
            </p>
          }
        </div>

        <label class="schedule-dialog__enabled">
          <input
            type="checkbox"
            data-testid="schedule-enabled"
            [checked]="enabled()"
            (change)="onEnabledChange($event)"
          />
          {{ 'testRunSchedule.dialog.enabled' | translate }}
        </label>

        @if (original()?.lastRunAt; as lastRunAt) {
          <section class="schedule-dialog__last" data-testid="schedule-last">
            <h3 class="schedule-dialog__label">{{ 'testRunSchedule.dialog.lastAttempt' | translate }}</h3>
            <div class="schedule-dialog__last-line">
              <span>{{ lastRunAt | date: 'dd.MM.yyyy, HH:mm' }}</span>
              @if (original()?.lastOutcome; as outcome) {
                <span
                  class="schedule-dialog__outcome"
                  data-testid="schedule-last-outcome"
                  [attr.data-outcome]="outcome"
                >
                  {{ 'testRunSchedule.outcome.' + outcome | translate }}
                </span>
              }
              @if (original()?.lastExecutionId; as executionId) {
                <a
                  class="schedule-dialog__last-link"
                  data-testid="schedule-last-execution"
                  [routerLink]="executionLink(executionId)"
                  (click)="cancel()"
                >
                  {{ 'testRunSchedule.dialog.openExecution' | translate }}
                </a>
              }
            </div>
            @if (original()?.lastMessage; as message) {
              <p class="schedule-dialog__last-message">{{ message }}</p>
            }
          </section>
        }

        @if (saveError(); as message) {
          <p class="schedule-dialog__error" role="alert" data-testid="schedule-save-error">
            <mat-icon aria-hidden="true">error</mat-icon>
            <span>{{ message }}</span>
          </p>
        }
      }
    }
  </mat-dialog-content>

  <mat-dialog-actions class="schedule-dialog__actions">
    @if (loadState() === 'loaded' && original() !== null) {
      <button
        mat-button
        type="button"
        class="schedule-dialog__delete"
        data-testid="schedule-delete"
        [disabled]="saving()"
        (click)="askDelete()"
      >
        <mat-icon aria-hidden="true">delete</mat-icon>
        {{ 'testRunSchedule.dialog.delete' | translate }}
      </button>
    }
    <span class="schedule-dialog__spacer"></span>
    <button mat-stroked-button type="button" data-testid="schedule-cancel" [disabled]="saving()" (click)="cancel()">
      {{ 'common.cancel' | translate }}
    </button>
    @if (loadState() === 'loaded') {
      <button mat-flat-button type="button" data-testid="schedule-save" [disabled]="!canSave()" (click)="save()">
        {{ 'testRunSchedule.dialog.save' | translate }}
      </button>
    }
  </mat-dialog-actions>

  <app-confirm-dialog
    [visible]="confirmingDelete()"
    [title]="'testRunSchedule.dialog.deleteTitle' | translate"
    [message]="'testRunSchedule.dialog.deleteMessage' | translate"
    [confirmLabel]="'testRunSchedule.dialog.delete' | translate"
    [cancelLabel]="'common.cancel' | translate"
    [busy]="saving()"
    (confirmed)="confirmDelete()"
    (cancelled)="cancelDelete()"
  />
</div>
